/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { FrequencyStore } from '../activity/frequency';
import bidders from '../bidders';
import { DataCollector } from '../collection/index';
import Config from '../config';
import Library from '../library';
import { Logger, LogLevel } from '../logger';
import { GlobalFactStore } from '../rules/facts/store';
import { SiteConfig } from '../site/config';
import { PageFactory } from '../site/index';
import { Page } from '../site/page';
import { CampaignType, SiteEnabledFeature } from '../types';
import { debugPixel, fetchJson, fillTemplate, getGlobalPrebidObj, getPrebidUserId } from '../util';

import type {
  SelectionEndpointPlacementData,
  SelectionEndpointResponsePayload,
  StoredData,
} from '../types';
import type {
  BidRequestObject,
  BidResponseObject,
  CallbackFunction,
  CompletedPrebidAuction,
  PrebidFormattedKeyValues,
  RtdModuleConfig,
  UserConsent,
} from '../types/prebid';

declare const __DEBUG_BUILD__: boolean;

const SELECTION_ENDPOINT_PATH = fillTemplate`/p/v1/${'propertyId'}/select?${'querystring'}`;

const logger = Logger.getInstance();

class AdTargetingEngine {
  private static instance: AdTargetingEngine | null;
  private factStore: GlobalFactStore | null = null;
  private initialised: boolean = false;
  private selectionDataRequired: boolean = false;
  private selectionDataReceived: boolean = false;
  private auctionNumber: number = 0;
  private page: Page | null = null;
  private siteConfig: SiteConfig | null = null;
  private version: string = Library.version;
  private recordTimings: boolean = false;

  private constructor() {
    if (
      __DEBUG_BUILD__ &&
      typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
    ) {
      this.recordTimings = Math.random() < 0.1;
    }

    try {
      const pb = getGlobalPrebidObj();
      let propertyId: string | undefined = undefined;
      if (pb) {
        const config = pb.getConfig();
        propertyId = config?.realTimeData?.dataProviders.find((p: any) => p.name === 'nodalsAi')
          ?.params?.propertyId;
      }
      debugPixel({
        en: 'libraryLoaded',
        pid: String(propertyId),
      });
      if (Array.isArray(window?.$nodals?.cmdQueue)) {
        const missedAuctionData = window.$nodals.cmdQueue.find(
          (queueObj: any) => queueObj?.cmd === 'getBidRequestData',
        );
        if (missedAuctionData) {
          const { config, reqBidsConfigObj, callback, userConsent, storedData } =
            missedAuctionData.data;
          debugPixel({
            en: 'auctionStartMissed',
            adUnitCodes: reqBidsConfigObj.adUnitCodes.join(','),
            pid: String(propertyId),
          });
          this.init(config);
          this.getBidRequestData(reqBidsConfigObj, callback, userConsent, storedData);
        }
        window.$nodals.cmdQueue = [];
      }
    } catch (e: any) {}
  }

  public static getInstance(): AdTargetingEngine {
    if (!AdTargetingEngine.instance) {
      AdTargetingEngine.instance = new AdTargetingEngine();
    }
    return AdTargetingEngine.instance;
  }

  public getVersion(): string {
    return this.version;
  }

  /**
   * Initialises the AdTargetingEngine with the provided configuration.
   *
   * IMPORTANT: This method is guaranteed to be called by the Prebid.js RTD provider
   * before ANY other API methods (getBidRequestData, getTargetingData, etc.).
   * The Prebid integration ensures init() is invoked first via #initialiseEngine().
   *
   * This method sets up all dependencies including:
   * - Config singleton initialization
   * - FactStore instance creation and configuration
   * - Logger configuration
   *
   * @param config - The RTD module configuration from Prebid.js
   */
  public init(config: RtdModuleConfig): void {
    if (this.initialised) {
      return;
    }
    Config.initialise(config);

    this.factStore = GlobalFactStore.getInstance();
    this.factStore.setConfig(Config.get());

    if (typeof config?.params?.lib?.logLevel === 'string') {
      logger.setLogLevel(
        LogLevel[config?.params?.lib?.logLevel.toUpperCase() as keyof typeof LogLevel],
      );
    }
    if (config?.params?.lib?.debug) {
      logger.setLogLevel(LogLevel.DEBUG);
    }
    this.initialised = true;
  }

  public getBidRequestData(
    requestObj: BidRequestObject,
    callback: CallbackFunction,
    userConsent: UserConsent,
    storedData: StoredData,
  ): void {
    const startTime = this.recordTimings ? performance.now() : 0;
    const adUnitsRepr = requestObj.adUnitCodes.join(',');

    logger.info(`Updating bid request data for ad units: ${adUnitsRepr}`);

    this.auctionNumber++;

    debugPixel({
      en: 'getBidRequestData',
      adUnitCodes: adUnitsRepr,
    });

    if (!this.isActive(storedData)) {
      logger.info('No campaigns and vscores not enabled, skipping bid request data processing.');
      callback();
      return;
    }

    const page = this.getPage(storedData, userConsent);

    const opportunity = this.page!.initialiseAuction(
      requestObj.adUnits,
      storedData.campaigns || [],
      requestObj.auctionId,
    );

    const eligibleCampaignTypes = [
      CampaignType.DIRECT,
      CampaignType.DEAL,
      CampaignType.SCORING_DIRECT,
      CampaignType.SCORING_DEAL,
    ];

    this.selectionDataReceived = false;
    const eligibleCampaignsByAdUnit: SelectionEndpointPlacementData[] =
      page.getEligibleCampaignsByAdUnit(requestObj.adUnitCodes, eligibleCampaignTypes);
    const hasEligibleCampaigns = eligibleCampaignsByAdUnit.some((eligibleAdUnitObj) => {
      return Array.isArray(eligibleAdUnitObj.campaigns) && eligibleAdUnitObj.campaigns.length > 0;
    });
    if (
      eligibleCampaignsByAdUnit.length === 0 ||
      (!hasEligibleCampaigns &&
        !this.getSiteConfig(storedData.property).hasFeatureEnabled(SiteEnabledFeature.VSCORES))
    ) {
      logger.info('No eligible placements or campaigns found, skipping selection endpoint call.');
      if (this.recordTimings) {
        debugPixel({
          en: 'methodTiming',
          method: 'getBidRequestData.total',
          duration: String(Math.round(performance.now() - startTime)),
          pid: String(storedData?.property?.id ?? ''),
        });
      }
      callback();
      return;
    }

    this.selectionDataRequired = true;
    debugPixel({
      en: 'selectionRequest',
      auctionNum: String(this.auctionNumber),
      adUnitCodes: requestObj.adUnitCodes.join(','),
      pid: String(storedData?.property?.id ?? ''),
    });
    const payload = this.generateSelectionRequestPayload(opportunity.id, eligibleCampaignsByAdUnit);
    logger.info(`Payload for selection endpoint: ${JSON.stringify(payload)}`);

    if (this.recordTimings) {
      debugPixel({
        en: 'methodTiming',
        method: 'getBidRequestData.preSelection',
        duration: String(Math.round(performance.now() - startTime)),
        pid: String(storedData?.property?.id ?? ''),
      });
    }

    fetchJson<SelectionEndpointResponsePayload>(this.generateEndpointUrl(userConsent), {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        logger.debug(`Selection endpoint response: ${JSON.stringify(response)}`);
        page.setPlacementKeyValues(requestObj.adUnitCodes, response.plcmts || [], response.l);
        this.selectionDataReceived = true;
        try {
          const targetingData: PrebidFormattedKeyValues = page.getKeyValuesForAdUnits(
            requestObj.adUnitCodes,
            [CampaignType.DEAL, CampaignType.SCORING_DEAL],
          );
          logger.info(`Deal targeting data for ad units: ${JSON.stringify(targetingData)}`);
          bidders.augmentBidRequest(requestObj, targetingData);
        } catch (e: any) {
          if (e instanceof Error) {
            logger.error(`Error in getBidRequestData: ${e.message}`);
            logger.error(`Stack trace:, ${e.stack}`);
          }
        }
      })
      .catch((error) => {
        logger.error(`Error fetching targeting data: ${error.message}`);
      })
      .finally(() => {
        if (this.recordTimings) {
          debugPixel({
            en: 'methodTiming',
            method: 'getBidRequestData.total',
            duration: String(Math.round(performance.now() - startTime)),
            pid: String(storedData?.property?.id ?? ''),
          });
        }
        callback();
      });
  }

  public onBidResponseEvent(
    // @ts-ignore
    bidResponse: BidResponseObject,
    // @ts-ignore
    userConsent: UserConsent,
    // @ts-ignore
    storedData: StoredData,
  ): void {}

  public onAuctionEndEvent(
    // @ts-ignore
    auctionDetails: CompletedPrebidAuction,
    // @ts-ignore
    userConsent: UserConsent,
    // @ts-ignore
    storedData: StoredData,
  ): void {
    if (!this.isActive(storedData)) {
      return;
    }

    const page = this.getPage(storedData, userConsent);
    page.endAuction(auctionDetails.adUnitCodes, auctionDetails.auctionId);
  }

  public getTargetingData(
    adUnitCodes: string[],
    // @ts-ignore
    userConsent: UserConsent,
    storedData: StoredData,
  ): PrebidFormattedKeyValues {
    const startTime = this.recordTimings ? performance.now() : 0;

    logger.info(`Retrieving targeting data for ad units: ${JSON.stringify(adUnitCodes)}`);

    if (!this.isActive(storedData)) {
      logger.info('No campaigns and vscores not enabled, skipping targeting data retrieval.');
      return {};
    }

    const page = this.getPage(storedData, userConsent);

    if (this.selectionDataRequired && !this.selectionDataReceived) {
      debugPixel({
        en: 'opportunityMissed',
        auctionNum: String(this.auctionNumber),
        adUnitCodes: adUnitCodes.join(','),
        pid: String(storedData?.property?.id ?? ''),
      });
    }

    let targetingData: PrebidFormattedKeyValues = {};
    try {
      targetingData = page.getKeyValuesForAdUnits(adUnitCodes, [
        CampaignType.DIRECT,
        CampaignType.SCORING_DIRECT,
      ]);
      logger.info(`Direct campaign targeting data for ad units: ${JSON.stringify(targetingData)}`);
    } catch (e: any) {
      if (e instanceof Error) {
        logger.error(`Error in getTargetingData: ${e.message}`);
        logger.debug(`Stack trace:, ${e.stack}`);
      }
    } finally {
      if (this.recordTimings) {
        debugPixel({
          en: 'methodTiming',
          method: 'getTargetingData',
          duration: String(Math.round(performance.now() - startTime)),
          pid: String(storedData?.property?.id ?? ''),
        });
      }
    }
    return targetingData;
  }

  private getSiteConfig(propertyConfig: any): SiteConfig {
    if (this.siteConfig === null) {
      this.siteConfig = SiteConfig.getInstance(propertyConfig);
    }
    return this.siteConfig;
  }

  private getPage(storedData: StoredData, userConsent: UserConsent): Page {
    this.updateFactStore(storedData);
    if (this.page === null) {
      const siteConfig = this.getSiteConfig(storedData.property);
      const config = Config.get();
      this.page = PageFactory.create(siteConfig, config, userConsent);
    } else {
      this.page.setUserConsent(userConsent);
    }
    return this.page;
  }

  private updateFactStore(storedData: StoredData): void {
    (this.factStore as GlobalFactStore).update(storedData.facts);
    if (storedData?.property?.acids) {
      (this.factStore as GlobalFactStore).setEnabledCids(storedData.property.acids);
    }
    DataCollector.getInstance().setConfig(storedData?.property?.dcfg);
  }

  private isActive(storedData: StoredData): boolean {
    const hasCampaigns = Array.isArray(storedData.campaigns) && storedData.campaigns.length > 0;
    const hasVscores = this.getSiteConfig(storedData.property).hasFeatureEnabled(
      SiteEnabledFeature.VSCORES,
    );
    return hasCampaigns || hasVscores;
  }

  private generateEndpointUrl(userConsent: UserConsent): string {
    const origin = Config.get().params.endpoint!.origin;
    const propertyId: string = Config.get().params.propertyId;
    const querystring: string = new URLSearchParams({
      gdpr_consent: userConsent?.gdpr?.consentString ?? '',
      gdpr: userConsent?.gdpr?.gdprApplies ? '1' : '0',
      us_privacy: userConsent?.uspConsent ?? '',
    }).toString();
    return (
      origin +
      SELECTION_ENDPOINT_PATH({
        propertyId: propertyId,
        querystring,
      })
    );
  }

  private generateSelectionRequestPayload(
    opportunityId: string,
    placements: SelectionEndpointPlacementData[],
  ): Record<string, any> {
    const globalFacts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      id: opportunityId,
      ref: globalFacts['page.url'] ?? '',
      pref: globalFacts['page.referrer'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      td: globalFacts['time.day'],
      th: globalFacts['time.hour'],
      tm: globalFacts['time.minutes'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
      plcmts: placements,
    };
    const publisherCohorts: String[] = globalFacts['publisher.cohort.ids'];
    if (publisherCohorts && publisherCohorts.length > 0) {
      payload.pcids = publisherCohorts;
    }
    const config = Config.get();
    const eidSource = config?.params.userIdEidSource || this.siteConfig?.userIdEidSource;
    if (eidSource) {
      const userId = getPrebidUserId(eidSource) ?? undefined;
      if (userId) {
        payload.uid = userId;
      }
    }
    const { xd, mainUserId, pcids } = DataCollector.getInstance().collect();
    // Merge the opportunity's decision-time page signals (psd/spd/pen/pdt,
    // locked in initialiseAuction) into the publisher-collected extra data.
    // Stock keys win any collision with publisher-defined dcfg keys.
    const mergedXd = { ...xd, ...FrequencyStore.getInstance().getPageSignals(opportunityId) };
    if (Object.keys(mergedXd).length > 0) {
      payload.xd = mergedXd;
    }
    // A dcfg-designated main user id supersedes the legacy eidsrc-derived uid.
    if (mainUserId) {
      payload.uid = mainUserId;
    }
    // A dcfg-collected pcids array overrides the vendor-derived publisher cohorts.
    if (pcids && pcids.length > 0) {
      payload.pcids = pcids;
    }
    return payload;
  }

  // Only use in tests
  public static __reset(): void {
    AdTargetingEngine.instance = null;
  }
}

export { AdTargetingEngine };
